/**
 * Redux Slice para gerenciar grupos de leads
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';

export interface Lead {
  id: string;
  name: string;
  address: string;
  city?: string;
  state?: string;
  neighborhood?: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  website?: string;
  coordinates?: { lat: number; lng: number };
  leadStatus?: 'novo' | 'contactado' | 'recusou' | 'semContato' | 'emContato' | 'fechado';
  followUps?: FollowUpEntry[];
}

export interface FollowUpEntry {
  id: string;
  date: string;
  type: string;
  notes: string;
}

export interface LeadGroup {
  id: string;
  name: string;
  leads: Lead[];
  createdAt: string;
  status: 'novo' | 'trabalhando' | 'finalizado';
  userId?: string;
  stats: {
    total: number;
    contactado: number;
    recusou: number;
    semContato: number;
    emContato: number;
    fechado: number;
  };
}

interface GroupsState {
  groups: LeadGroup[];
  loading: boolean;
  error: string | null;
}

const initialState: GroupsState = {
  groups: [],
  loading: false,
  error: null,
};

// Async Thunks
export const fetchGroups = createAsyncThunk(
  'groups/fetchGroups', 
  async ({ userId, isAdmin }: { userId?: string; isAdmin?: boolean }) => {
    return await api.groups.list(userId, isAdmin);
  }
);

export const createGroupAsync = createAsyncThunk(
  'groups/createGroup',
  async ({ name, leads, userId }: { name: string; leads: any[]; userId?: string }) => {
    const groupId = `group_${Date.now()}`;
    const formattedLeads = leads.map((lead, index) => ({
      id: `lead_${Date.now()}_${index}`,
      name: lead.name || 'Sem nome',
      address: lead.address || 'Endereço não disponível',
      phone: lead.phone,
      whatsapp: lead.whatsapp,
      email: lead.email,
      website: lead.website,
      coordinates: lead.coordinates,
      status: 'novo',
    }));

    await api.groups.create({
      id: groupId,
      name,
      status: 'novo',
      leads: formattedLeads,
      userId,
    });

    return { id: groupId, name, leads: formattedLeads, userId };
  }
);

export const updateGroupStatusAsync = createAsyncThunk(
  'groups/updateStatus',
  async ({ groupId, status }: { groupId: string; status: string }) => {
    await api.groups.updateStatus(groupId, status);
    return { groupId, status };
  }
);

export const deleteGroupAsync = createAsyncThunk(
  'groups/deleteGroup',
  async (groupId: string) => {
    await api.groups.delete(groupId);
    return groupId;
  }
);

export const updateLeadStatusAsync = createAsyncThunk(
  'groups/updateLeadStatus',
  async ({ leadId, status }: { leadId: string; status: string }) => {
    await api.leads.updateStatus(leadId, status);
    return { leadId, status };
  }
);

export const addFollowUpAsync = createAsyncThunk(
  'groups/addFollowUp',
  async ({ leadId, followUp }: { leadId: string; followUp: { type: string; notes: string } }) => {
    await api.leads.addFollowUp(leadId, followUp);
    return { leadId, followUp: { ...followUp, id: `followup_${Date.now()}`, date: new Date().toISOString() } };
  }
);

const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    // Reducer local para compatibilidade (quando não usar backend)
    addGroup: (state, action: PayloadAction<{ name: string; leads: any[] }>) => {
      const newLeads: Lead[] = action.payload.leads.map((lead, index) => ({
        id: `lead_${Date.now()}_${index}`,
        name: lead.name || 'Sem nome',
        address: lead.address || lead.vicinity || 'Endereço não disponível',
        phone: lead.formatted_phone_number || lead.phone,
        whatsapp: lead.whatsapp,
        email: lead.email,
        website: lead.website,
        coordinates: lead.coordinates,
        leadStatus: 'novo',
        followUps: [],
      }));

      const newGroup: LeadGroup = {
        id: `group_${Date.now()}`,
        name: action.payload.name,
        leads: newLeads,
        createdAt: new Date().toISOString(),
        status: 'novo',
        stats: {
          total: newLeads.length,
          contactado: 0,
          recusou: 0,
          semContato: 0,
          emContato: 0,
          fechado: 0,
        },
      };

      state.groups.unshift(newGroup);
    },

    setGroups: (state, action: PayloadAction<LeadGroup[]>) => {
      state.groups = action.payload;
    },

    updateLeadStatusLocal: (
      state,
      action: PayloadAction<{ groupId: string; leadId: string; status: string }>
    ) => {
      const group = state.groups.find((g) => g.id === action.payload.groupId);
      if (group) {
        const lead = group.leads.find((l) => l.id === action.payload.leadId);
        if (lead) {
          lead.leadStatus = action.payload.status as any;
        }
      }
    },

    updateGroupStatusLocal: (
      state,
      action: PayloadAction<{ groupId: string; status: LeadGroup['status'] }>
    ) => {
      const group = state.groups.find((g) => g.id === action.payload.groupId);
      if (group) {
        group.status = action.payload.status;
      }
    },

    deleteGroupLocal: (state, action: PayloadAction<string>) => {
      state.groups = state.groups.filter((g) => g.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch Groups
    builder.addCase(fetchGroups.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchGroups.fulfilled, (state, action) => {
      state.loading = false;
      state.groups = action.payload;
    });
    builder.addCase(fetchGroups.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Erro ao carregar grupos';
    });

    // Create Group
    builder.addCase(createGroupAsync.fulfilled, (state, action) => {
      const { id, name, leads } = action.payload;
      const newGroup: LeadGroup = {
        id,
        name,
        leads,
        createdAt: new Date().toISOString(),
        status: 'novo',
        stats: {
          total: leads.length,
          contactado: 0,
          recusou: 0,
          semContato: 0,
          emContato: 0,
          fechado: 0,
        },
      };
      state.groups.unshift(newGroup);
    });

    // Update Group Status
    builder.addCase(updateGroupStatusAsync.fulfilled, (state, action) => {
      const group = state.groups.find((g) => g.id === action.payload.groupId);
      if (group) {
        group.status = action.payload.status as any;
      }
    });

    // Delete Group
    builder.addCase(deleteGroupAsync.fulfilled, (state, action) => {
      state.groups = state.groups.filter((g) => g.id !== action.payload);
    });

    // Update Lead Status
    builder.addCase(updateLeadStatusAsync.fulfilled, (state, action) => {
      const { leadId, status } = action.payload;
      // Atualizar o lead em todos os grupos
      state.groups.forEach(group => {
        const lead = group.leads.find(l => l.id === leadId);
        if (lead) {
          lead.leadStatus = status as any;
        }
      });
    });

    // Add Follow-up
    builder.addCase(addFollowUpAsync.fulfilled, (state, action) => {
      const { leadId, followUp } = action.payload;
      // Adicionar follow-up ao lead em todos os grupos
      state.groups.forEach(group => {
        const lead = group.leads.find(l => l.id === leadId);
        if (lead) {
          if (!lead.followUps) {
            lead.followUps = [];
          }
          lead.followUps.unshift(followUp as any);
        }
      });
    });
  },
});

export const { 
  addGroup, 
  setGroups,
  updateLeadStatusLocal, 
  updateGroupStatusLocal, 
  deleteGroupLocal 
} = groupsSlice.actions;

export default groupsSlice.reducer;
